import { useState, useMemo } from "react";
import { Archive } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useGoogleDocs } from "@/hooks/useGoogleDocs";
import { useAutosave } from "@/hooks/useAutosave";
import { useMultiSectionExport } from "@/hooks/useMultiSectionExport";
import { SectionActionBar } from "@/components/multisection/SectionActionBar";
import { SectionCard } from "@/components/multisection/SectionCard";

export const MultiSectionDisplay = ({ data, onSave, isSaved, exportMeta, resourcesUsed, generationContext, aiModel }) => {
  const { api } = useAuth();
  const { exportToGoogleDocs, gdocsExporting } = useGoogleDocs();
  const [copiedSections, setCopiedSections] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({});
  const [saving, setSaving] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [editedContent, setEditedContent] = useState({});
  const [regenOpen, setRegenOpen] = useState(null);
  const [regenInstruction, setRegenInstruction] = useState("");
  const [regenLoading, setRegenLoading] = useState({});
  const [regenSuccess, setRegenSuccess] = useState({});
  const [regenError, setRegenError] = useState({});

  const sections = useMemo(() => data.sections || {}, [data.sections]);
  const sectionNames = useMemo(() => data.section_names || Object.keys(sections), [data.section_names, sections]);

  const { autosaveStatus, lastSavedTime, hasUnsavedEdits, deleteDraft } = useAutosave({
    api, sections, editedContent, isSaved, generationContext, exportMeta,
  });

  const {
    copiedAll, copyAll, copiedMarkdown, copyMarkdown, copiedHtml, copyHtml,
    exporting, handleExportPdf, openInGoogleDocs,
  } = useMultiSectionExport({ sections, sectionNames, editedContent, exportMeta, api, exportToGoogleDocs });

  const [showNavBlocker, setShowNavBlocker] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const blocker = {
    state: showNavBlocker ? "blocked" : "idle",
    reset: () => { setShowNavBlocker(false); setPendingNavigation(null); },
    proceed: () => { setShowNavBlocker(false); if (pendingNavigation) window.location.href = pendingNavigation; },
  };

  const copySection = async (sectionName) => {
    const content = editedContent[sectionName] || sections[sectionName] || "";
    try { await navigator.clipboard.writeText(content); } catch {
      const ta = document.createElement("textarea"); ta.value = content;
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopiedSections((prev) => ({ ...prev, [sectionName]: true }));
    setTimeout(() => setCopiedSections((prev) => ({ ...prev, [sectionName]: false })), 2000);
    toast.success(`"${sectionName}" copied!`);
  };

  const toggleCollapse = (sectionName) => setCollapsedSections((prev) => ({ ...prev, [sectionName]: !prev[sectionName] }));
  const startEdit = (sectionName) => {
    setEditingSection(sectionName);
    if (!editedContent[sectionName]) setEditedContent((prev) => ({ ...prev, [sectionName]: sections[sectionName] || "" }));
  };
  const saveEdit = () => { setEditingSection(null); toast.success("Section updated"); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const mergedSections = { ...sections };
      Object.keys(editedContent).forEach((key) => { mergedSections[key] = editedContent[key]; });
      await onSave(mergedSections);
      await deleteDraft();
      toast.success("Saved to history! +5 XP");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleBlockerSave = async () => { await handleSave(); if (blocker.state === "blocked") blocker.proceed(); };

  const handleRegenerateSection = async (sectionName) => {
    if (!aiModel || !generationContext) return;
    setRegenLoading((p) => ({ ...p, [sectionName]: true }));
    setRegenError((p) => ({ ...p, [sectionName]: null }));
    const currentSections = { ...sections };
    Object.keys(editedContent).forEach((k) => { currentSections[k] = editedContent[k]; });
    try {
      const resp = await api.post("/generate/regenerate-section", {
        section_key: sectionName, section_title: sectionName,
        full_content: currentSections, generation_context: generationContext,
        ai_model: aiModel, regeneration_instruction: regenInstruction,
      });
      setEditedContent((p) => ({ ...p, [sectionName]: resp.data.new_content }));
      setRegenOpen(null); setRegenInstruction("");
      setRegenSuccess((p) => ({ ...p, [sectionName]: true }));
      setTimeout(() => setRegenSuccess((p) => ({ ...p, [sectionName]: false })), 1500);
    } catch {
      setRegenError((p) => ({ ...p, [sectionName]: "Regeneration failed. Try again." }));
    } finally {
      setRegenLoading((p) => ({ ...p, [sectionName]: false }));
    }
  };

  const formatTime = (date) => date ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="space-y-4" data-testid="multi-section-display">
      <SectionActionBar
        copiedAll={copiedAll} onCopyAll={copyAll} onSave={onSave ? handleSave : null} saving={saving} isSaved={isSaved}
        sectionNames={sectionNames} collapsedSections={collapsedSections}
        onToggleAll={() => {
          const allCollapsed = sectionNames.every((n) => collapsedSections[n]);
          const newState = {};
          sectionNames.forEach((n) => { newState[n] = !allCollapsed; });
          setCollapsedSections(newState);
        }}
        onExportPdf={handleExportPdf} exporting={exporting} onCopyMarkdown={copyMarkdown} copiedMarkdown={copiedMarkdown}
        onOpenGoogleDocs={openInGoogleDocs} gdocsExporting={gdocsExporting} onCopyHtml={copyHtml} copiedHtml={copiedHtml}
        autosaveStatus={autosaveStatus} lastSavedTime={lastSavedTime} hasUnsavedEdits={hasUnsavedEdits} formatTime={formatTime}
      />

      {resourcesUsed?.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]" data-testid="resources-used-tag">
          <Archive size={14} className="shrink-0" />
          <span>Generated with: {resourcesUsed.join(", ")}</span>
        </div>
      )}

      {sectionNames.map((sectionName, idx) => (
        <SectionCard
          key={sectionName} sectionName={sectionName} idx={idx}
          content={editedContent[sectionName] || sections[sectionName] || ""}
          isCollapsed={collapsedSections[sectionName]} onToggleCollapse={toggleCollapse}
          isEditing={editingSection === sectionName} editedValue={editedContent[sectionName]}
          onStartEdit={startEdit} onSaveEdit={saveEdit}
          onDiscardEdit={(name) => { setEditingSection(null); setEditedContent((prev) => { const n = { ...prev }; delete n[name]; return n; }); }}
          onEditChange={(name, value) => setEditedContent((prev) => ({ ...prev, [name]: value }))}
          isCopied={copiedSections[sectionName]} onCopy={copySection}
          aiModel={aiModel} generationContext={generationContext}
          regenOpen={regenOpen === sectionName}
          onToggleRegen={(name) => { setRegenOpen(name === null ? null : (regenOpen === name ? null : name)); setRegenInstruction(""); setRegenError((p) => ({ ...p, [sectionName]: null })); }}
          regenInstruction={regenInstruction} onRegenInstructionChange={setRegenInstruction}
          regenLoading={regenLoading[sectionName]} regenSuccess={regenSuccess[sectionName]} regenError={regenError[sectionName]}
          onRegenerate={handleRegenerateSection}
        />
      ))}

      <Dialog open={blocker.state === "blocked"} onOpenChange={() => blocker.reset()}>
        <DialogContent className="bg-[var(--bg-raised)] border-[var(--border-strong)] text-[var(--text-primary)] max-w-md" data-testid="unsaved-nav-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--text-primary)]">You have unsaved edits</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            Your changes have been autosaved as a draft. You can recover them from Draft Recovery on the History page. Save to History now to keep them permanently.
          </p>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => blocker.reset()} className="text-[var(--text-secondary)]" data-testid="nav-dialog-stay">Stay on Page</Button>
            <Button variant="ghost" onClick={() => blocker.proceed()} className="text-[var(--text-secondary)]" data-testid="nav-dialog-continue">Continue Without Saving</Button>
            {onSave && (
              <Button onClick={handleBlockerSave} className="bg-[#FF4400] hover:bg-[#FF6633] text-white" data-testid="nav-dialog-save">Save to History</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};
